import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SafetyInsights from '../../components/SafetyInsights';
import AppLayout from '../../components/AppLayout';
import { useRealtime } from '../../providers/RealtimeProvider';
import { getDriverSafetyEvents, getDriverSafetyReport } from '../../services/safetyService';
import SafetyAcknowledgementsReport from './SafetyAcknowledgementsReport';
import SafetyLoginMessage from '../settings/SafetyLoginMessage';
import InsuranceSafetyReport from './InsuranceSafetyReport';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const scoreLabel = (row) => {
  if (!row.provisional && row.score != null) return Number(row.score).toFixed(1);
  if (row.liveScore != null) return `${Number(row.liveScore).toFixed(1)} estimate`;
  return 'Not enough data';
};
const eventLabels = { acceleration: 'Rapid acceleration', braking: 'Harsh braking', cornering: 'Hard cornering', speeding: 'Speeding' };

function Bar({ label, value, max, color = '#2563eb' }) {
  const width = max ? Math.max(value ? 3 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 52px', gap: 10, alignItems: 'center' }}>
      <span>{label}</span>
      <div style={{ height: 12, background: 'var(--surface-muted, #e2e8f0)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ width: `${width}%`, height: '100%', background: color }} />
      </div>
      <strong style={{ textAlign: 'right' }}>{value}</strong>
    </div>
  );
}

function RoadBehaviorReport() {
  const { socket, connected } = useRealtime();
  const [month, setMonth] = useState(currentMonth);
  const [driverId, setDriverId] = useState('');
  const [eventType, setEventType] = useState('');
  const [data, setData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [page, setPage] = useState(1);
  const [eventPage, setEventPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const realtimeRefreshTimer = useRef(null);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    if (!background) setError('');
    try {
      const params = { month, driverId: driverId || undefined, eventType: eventType || undefined };
      const [response, eventsResponse] = await Promise.all([
        getDriverSafetyReport({ ...params, page, limit: 25 }),
        getDriverSafetyEvents({ ...params, page: eventPage, limit: 25 }),
      ]);
      setData(response.data);
      setEventData(eventsResponse.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load the driver safety report.');
    } finally {
      if (!background) setLoading(false);
    }
  }, [driverId, eventPage, eventType, month, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (month !== currentMonth()) return undefined;
    const timer = setInterval(() => load({ background: true }), 60_000);
    return () => clearInterval(timer);
  }, [load, month]);
  useEffect(() => {
    if (!socket) return undefined;
    const handleScoreUpdate = (payload = {}) => {
      if (payload.month && payload.month !== month) return;
      if (driverId && payload.driverId && payload.driverId !== driverId) return;
      if (realtimeRefreshTimer.current) return;
      realtimeRefreshTimer.current = setTimeout(() => {
        realtimeRefreshTimer.current = null;
        load({ background: true });
      }, 750);
    };
    socket.on('safety:score_updated', handleScoreUpdate);
    return () => {
      socket.off('safety:score_updated', handleScoreUpdate);
      if (realtimeRefreshTimer.current) clearTimeout(realtimeRefreshTimer.current);
      realtimeRefreshTimer.current = null;
    };
  }, [driverId, load, month, socket]);
  const summary = data?.summary || {};
  const distribution = summary.distribution || {};
  const eventRateBasisMiles = Number(summary.eventRateBasisMiles || 100);
  const rates = summary.eventRates || summary.eventRatesPer100Miles || {};
  const maxDistribution = Math.max(1, ...Object.values(distribution).map(Number));
  const maxRates = Math.max(1, ...Object.values(rates).map(Number));
  const drivers = useMemo(() => data?.drivers || [], [data?.drivers]);
  const trends = useMemo(() => data?.trends || [], [data?.trends]);
  const maxTrendMiles = Math.max(1, ...trends.map((trend) => Number(trend.validMiles || 0)));
  const knownDrivers = useMemo(() => {
    const seen = new Map();
    drivers.forEach((row) => seen.set(row.driverId, row.driverName || row.driverId));
    return Array.from(seen.entries());
  }, [drivers]);

  return (
    <>
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-body">
          <div className="form-grid" style={{ alignItems: 'end' }}>
            <div><label htmlFor="safetyMonth">Calendar month</label><input id="safetyMonth" type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPage(1); setEventPage(1); }} /></div>
            <div><label htmlFor="safetyDriver">Driver</label><select id="safetyDriver" value={driverId} onChange={(e) => { setDriverId(e.target.value); setPage(1); setEventPage(1); }}><option value="">All drivers</option>{knownDrivers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></div>
            <div><label htmlFor="safetyEvent">Event</label><select id="safetyEvent" value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(1); setEventPage(1); }}><option value="">All road events</option>{Object.entries(eventLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            <button type="button" className="btn btn-primary" onClick={() => load()} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          </div>
          <p className="hint" style={{ margin: '12px 0 0' }}>
            {month === currentMonth()
              ? `Live month-to-date scoring · ${connected ? 'push updates connected' : 'automatic 60-second refresh'}${data?.generatedAt ? ` · updated ${new Date(data.generatedAt).toLocaleTimeString()}` : ''}`
              : 'Historical month · values are final unless telemetry is backfilled'}
          </p>
        </div>
      </div>
      {error ? <div className="feedback error">{error}</div> : null}
      <section className="ops-briefing-metrics" aria-label="Fleet safety summary">
        <div className="ops-briefing-metric ops-briefing-metric--info"><span>Month-to-date average</span><strong>{summary.liveFleetAverage == null ? 'N/A' : summary.liveFleetAverage}</strong><em>live driver estimates / 100</em></div>
        <div className="ops-briefing-metric ops-briefing-metric--good"><span>Valid mileage</span><strong>{Number(summary.validMiles || 0).toFixed(1)}</strong><em>{Math.round(Number(summary.scoredCoverage || 0) * 100)}% speed-limit coverage</em></div>
        <div className="ops-briefing-metric ops-briefing-metric--warning"><span>Coaching review</span><strong>{summary.driversNeedingReview || 0}</strong><em>qualified scores below 70</em></div>
        <div className="ops-briefing-metric"><span>Safety context</span><strong>{Number(summary.context?.sos || 0) + Number(summary.context?.staleTripCancellations || 0) + Number(summary.context?.trackingLoss || 0)}</strong><em>{summary.context?.sos || 0} SOS · {summary.context?.staleTripCancellations || 0} stale · {summary.context?.trackingLoss || 0} signal loss</em></div>
      </section>

      <SafetyInsights insights={summary.safetyInsights} />

      <section className="ops-primary-grid" style={{ marginTop: 18 }}>
        <div className="panel"><div className="panel-header"><div><h3>Score distribution</h3><p className="panel-subtitle">Provisional drivers remain visible without a percentage.</p></div></div><div className="panel-body" style={{ display: 'grid', gap: 10 }}>
          <Bar label="Excellent 90–100" value={distribution.excellent || 0} max={maxDistribution} color="#16a34a" />
          <Bar label="Good 80–89" value={distribution.good || 0} max={maxDistribution} color="#2563eb" />
          <Bar label="Watch 70–79" value={distribution.watch || 0} max={maxDistribution} color="#f59e0b" />
          <Bar label="Below 70" value={distribution.highRisk || 0} max={maxDistribution} color="#dc2626" />
          <Bar label="Not enough data" value={distribution.provisional || 0} max={maxDistribution} color="#64748b" />
        </div></div>
        <div className="panel"><div className="panel-header"><div><h3>Event rates</h3><p className="panel-subtitle">Events per {eventRateBasisMiles.toLocaleString()} valid miles, configured in Company Settings. Road behavior only; context does not affect scores.</p></div></div><div className="panel-body" style={{ display: 'grid', gap: 10 }}>
          {Object.entries(eventLabels).map(([key, label]) => <Bar key={key} label={label} value={rates[key] || 0} max={maxRates} color={key === 'braking' ? '#dc2626' : key === 'speeding' ? '#7c3aed' : '#2563eb'} />)}
        </div></div>
      </section>

      <div className="panel" style={{ marginTop: 18 }}><div className="panel-header"><div><h3>Score trend</h3><p className="panel-subtitle">Fleet average and valid mileage by calendar month.</p></div></div><div className="panel-body" style={{ display: 'grid', gap: 10 }}>
        {trends.length ? trends.map((trend) => <div key={trend.month} style={{ display: 'grid', gap: 6 }}><strong>{trend.month}</strong><Bar label="Fleet score" value={trend.score} max={100} color="#0ea5e9" /><Bar label="Valid miles" value={trend.validMiles} max={maxTrendMiles} color="#16a34a" /></div>) : <p className="hint">No qualified monthly trend is available yet.</p>}
      </div></div>

      <div className="panel" style={{ marginTop: 18, overflowX: 'auto' }}><div className="panel-header"><div><h3>Drivers</h3><p className="panel-subtitle">Live estimates begin with the first valid mileage. They qualify for fleet and insurance calculations at {Number(summary.minimumMileage || 0).toLocaleString()} miles.</p></div></div><table className="data-table"><thead><tr><th>Driver</th><th>Cab</th><th>Score</th><th>Valid miles</th><th>Coverage</th><th>Events</th></tr></thead><tbody>
        {!drivers.length && !loading ? <tr><td colSpan="6">No safety data for this month.</td></tr> : drivers.map((row) => <tr key={row.driverId}><td><button type="button" className="btn btn-subtle" onClick={() => { setDriverId(row.driverId); setPage(1); setEventPage(1); }}>{row.driverName || row.driverId}</button></td><td>{row.cabNumber || '—'}</td><td><strong>{scoreLabel(row)}</strong></td><td>{Number(row.validMiles || 0).toFixed(1)}</td><td>{Math.round(Number(row.coverage || 0) * 100)}%</td><td>{Object.keys(eventLabels).reduce((sum, type) => sum + Number(row.eventCounts?.[type] || 0), 0)}</td></tr>)}
      </tbody></table><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 14 }}><button type="button" className="btn btn-subtle" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button><span style={{ alignSelf: 'center' }}>Page {page} of {Math.max(1, Math.ceil(Number(data?.pagination?.totalDrivers || 0) / 25))}</span><button type="button" className="btn btn-subtle" disabled={page * 25 >= Number(data?.pagination?.totalDrivers || 0) || loading} onClick={() => setPage((value) => value + 1)}>Next</button></div></div>

      <div className="panel" style={{ marginTop: 18, overflowX: 'auto' }}><div className="panel-header"><div><h3>Event drill-down</h3><p className="panel-subtitle">Confidence and coverage identify which Mapbox-enriched events were scoreable.</p></div></div><table className="data-table"><thead><tr><th>Time</th><th>Driver</th><th>Trip</th><th>Type</th><th>Severity</th><th>Duration</th><th>Confidence</th><th>Location</th></tr></thead><tbody>
        {!(eventData?.events || []).length && !loading ? <tr><td colSpan="8">No matching events.</td></tr> : (eventData?.events || []).map((event) => { const coordinates = event.point?.coordinates; return <tr key={event._id}><td>{new Date(event.startedAt).toLocaleString()}</td><td>{event.driverId}</td><td>{event.bookingId ? <a href={`/bookings/${event.bookingId}`}>Open trip</a> : 'On-duty'}</td><td>{eventLabels[event.type] || event.type}</td><td>{event.severity}</td><td>{Math.round(event.durationSeconds || 0)} sec</td><td>{event.confidence == null ? 'N/A' : `${Math.round(event.confidence * 100)}% / ${Math.round(Number(event.coverage || 0) * 100)}%`}</td><td>{coordinates?.length === 2 ? <a href={`https://www.google.com/maps?q=${coordinates[1]},${coordinates[0]}`} target="_blank" rel="noreferrer">Map</a> : '—'}</td></tr>; })}
      </tbody></table><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 14 }}><button type="button" className="btn btn-subtle" disabled={eventPage <= 1 || loading} onClick={() => setEventPage((value) => Math.max(1, value - 1))}>Previous</button><span style={{ alignSelf: 'center' }}>Page {eventPage} of {Math.max(1, Math.ceil(Number(eventData?.pagination?.total || 0) / 25))}</span><button type="button" className="btn btn-subtle" disabled={eventPage * 25 >= Number(eventData?.pagination?.total || 0) || loading} onClick={() => setEventPage((value) => value + 1)}>Next</button></div></div>
    </>
  );
}

const SAFETY_TABS = [
  { id: 'overview', label: 'Safety overview' },
  { id: 'acknowledgements', label: 'Acknowledgements' },
  { id: 'login-message', label: 'Login message' },
  { id: 'insurance-report', label: 'Insurance report' },
];

export default function DriverSafetyReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = SAFETY_TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'overview';

  const selectTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <AppLayout
      title="Driver Safety"
      subtitle="Safety scoring, login notices, and acknowledgement records in one place."
    >
      <div
        className="map-toggle"
        role="tablist"
        aria-label="Driver safety sections"
        style={{ justifyContent: 'flex-start', padding: '0 0 24px', flexWrap: 'wrap' }}
      >
        {SAFETY_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`btn btn-ghost ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => selectTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <RoadBehaviorReport />}
      {activeTab === 'acknowledgements' && <SafetyAcknowledgementsReport embedded />}
      {activeTab === 'login-message' && <SafetyLoginMessage embedded />}
      {activeTab === 'insurance-report' && <InsuranceSafetyReport />}
    </AppLayout>
  );
}
